import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logo from '../assets/logo.png';
import { API_BASE_URL } from '../lib/apiClient';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!token) {
      setError('Invalid or missing reset link.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, confirmPassword: confirm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Could not reset password.');
      } else {
        setMessage(data.message || 'Password updated. You can log in now.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-screen min-h-screen bg-white font-montserrat">
      <div className="absolute left-12 top-12">
        <img src={logo} alt="USC Automate Logo" className="w-48 object-contain" />
      </div>
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-neutral-grey-300 bg-white p-8 shadow-custom">
          <h1 className="text-center text-xl font-semibold text-neutral-dark-500">Set new password</h1>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}
            {message && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                {message}
              </div>
            )}
            {!token && (
              <p className="text-sm text-red-600">This link is invalid. Request a new reset from the login page.</p>
            )}
            <div>
              <label className="mb-1 block text-xs text-neutral-dark-200">New password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-neutral-grey-300 px-3 py-2 text-sm"
                autoComplete="new-password"
              />
              <p className="mt-1 text-xs text-neutral-dark-200">
                At least 6 characters with upper, lower, and a number.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs text-neutral-dark-200">Confirm password</label>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-neutral-grey-300 px-3 py-2 text-sm"
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full rounded-lg bg-primary-500 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Update password'}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-neutral-dark-200">
            <Link to="/login" className="text-primary-600 hover:underline">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
